import * as React from 'react';
import {Query, Mutation} from 'react-apollo';
import {Helmet} from 'react-helmet';

import {headTitle} from 'shells/document/head-title';
import type {Tag} from 'shells/tags-editor/tags-editor';

import type {
    UpdateLeadCustomFieldValue as UpdateLeadCustomFieldValueMutation,
    UpdateLeadCustomFieldValueVariables as UpdateLeadCustomFieldValueMutationVariables,
    UpdateLeadInputFieldInput,
    Lead as LeadFragment,
    GetLead as GetLeadQuery,
    GetLeadVariables as GetLeadQueryVariables,
    GetTags as GetTagsQuery,
    GetTagsVariables as GetTagsQueryVariables,
    SessionUser,
} from 'nutshell-graphql-types';

import {GetSessionUser} from '../../../core/get-session-user';
import {GET_TAGS_LIMIT} from '../../../core/select/hooks/use-get-tags';

import LEAD_FRAGMENT from '../../shared/graphql/fragments/lead-fragment.graphql';
import UPDATE_LEAD_CUSTOM_FIELD_VALUE from '../../shared/graphql/mutations/update-lead-custom-field-value.graphql';
import GET_LEAD from '../../shared/graphql/queries/get-lead.graphql';
import GET_TAGS from '../../shared/graphql/queries/get-tags.graphql';

import {LeadHeaderContainer} from './lead-header-container';
import {LeadSubheaderContainer} from './lead-subheader-container';

import styles from './lead-header-left.module.css';

type OwnProps = {
    leadId: string;
};

type GraphQLProps = {
    lead: LeadFragment | null | undefined;
    user: SessionUser | null | undefined;
    isLoading: boolean;
    updateLead: (arg1: UpdateLeadInputFieldInput) => Promise<any>;
    fetchTags: (searchTerm: string) => Promise<any>;
};

type Props = OwnProps & GraphQLProps;

class LeadHeaderLeftContainer extends React.PureComponent<Props> {
    render() {
        const lead = this.props.lead;

        if (this.props.isLoading || !lead) {
            return <div style={{width: '100vw', height: '100vh', backgroundColor: '#fff'}} />;
        }

        return (
            <div className={styles.container}>
                <Helmet>
                    <title>{headTitle(['Lead', lead.name])}</title>
                </Helmet>
                <LeadHeaderContainer
                    lead={lead}
                    user={this.props.user}
                    onUpdateLeadName={this.updateLeadName}
                    fetchTags={this.props.fetchTags}
                    showButtons={false}
                />
                <LeadSubheaderContainer
                    lead={lead}
                    onUpdateLeadConfidence={this.updateLeadConfidence}
                    onUpdateLeadCreatedDate={this.updateLeadCreatedDate}
                    onUpdateLeadDueDate={this.updateLeadDueDate}
                    onUpdateLeadClosedDate={this.updateLeadClosedDate}
                />
            </div>
        );
    }

    updateLeadConfidence = (newConfidence: number) => {
        this.props.updateLead({
            id: this.props.leadId,
            confidence: newConfidence,
        });
    };

    updateLeadName = (newName: string): Promise<any> => {
        return this.props.updateLead({
            id: this.props.leadId,
            // If the lead name is empty, save null
            description: newName.length ? newName : null,
        });
    };

    updateLeadDueDate = (newDate?: Date | null) => {
        this.props.updateLead({
            id: this.props.leadId,
            dueTime: newDate ? newDate.getTime() / 1000 : null,
        });
    };

    updateLeadCreatedDate = (newDate?: Date | null) => {
        this.props.updateLead({
            id: this.props.leadId,
            createdTime: newDate ? Math.floor(newDate.getTime()) / 1000 : null,
        });
    };

    updateLeadClosedDate = (newDate?: Date | null) => {
        this.props.updateLead({
            id: this.props.leadId,
            closedTime: newDate ? Math.floor(newDate.getTime()) / 1000 : null,
        });
    };

    handleUpdateTags = (tags: Tag[]) => {
        this.props.updateLead({
            id: this.props.leadId,
            // @ts-expect-error
            tags: tags,
        });
    };
}

class LeadQuery extends Query<GetLeadQuery, GetLeadQueryVariables> {}

class TagsQuery extends Query<GetTagsQuery, GetTagsQueryVariables> {}

class UpdateLeadMutation extends Mutation<
    UpdateLeadCustomFieldValueMutation,
    UpdateLeadCustomFieldValueMutationVariables
> {}

export const LeadHeaderLeft = (props: OwnProps) => (
    <GetSessionUser>
        {({user}) => (
            <TagsQuery
                query={GET_TAGS}
                // @ts-expect-error
                variables={{limit: GET_TAGS_LIMIT, input: {entityType: 'LEAD'}}}
            >
                {({fetchMore: fetchMoreTags}) => {
                    return (
                        <LeadQuery query={GET_LEAD} variables={{id: props.leadId}}>
                            {({data, loading}) => {
                                const leadData = data ? data.lead : null;

                                return (
                                    <UpdateLeadMutation mutation={UPDATE_LEAD_CUSTOM_FIELD_VALUE}>
                                        {(updateLeadMutation) => (
                                            <LeadHeaderLeftContainer
                                                {...props}
                                                lead={leadData}
                                                user={user}
                                                isLoading={loading}
                                                fetchTags={(searchTerm: string) => {
                                                    // @ts-expect-error
                                                    return fetchMoreTags({
                                                        query: GET_TAGS,
                                                        variables: {
                                                            limit: GET_TAGS_LIMIT,
                                                            input: {
                                                                entityType: 'LEAD',
                                                                searchTerm: searchTerm,
                                                            },
                                                        },
                                                        updateQuery: (
                                                            previousResult,
                                                            {fetchMoreResult}
                                                        ) => {
                                                            if (!fetchMoreResult || !previousResult)
                                                                return;

                                                            return {
                                                                tags: {
                                                                    __typename: 'TagsConnection',
                                                                    ...fetchMoreResult.tags,
                                                                },
                                                            };
                                                        },
                                                    });
                                                }}
                                                updateLead={(lead) => {
                                                    // We need to modify the leadName a little bit, because the name is
                                                    // generated server side from the description.
                                                    let leadName = '';
                                                    let newLeadDueDate;
                                                    if (leadData) {
                                                        if (lead.description === undefined) {
                                                            // We aren't changing the lead description on this update. Use the existing name
                                                            leadName = leadData.name;
                                                        } else if (lead.description === null) {
                                                            // Until we can get the response from the server, let the user know it's going to change
                                                            leadName = 'determining lead name…';
                                                        } else {
                                                            // We are setting the lead description, so use the new value
                                                            leadName = lead.description;
                                                        }

                                                        if (lead.dueTime === undefined) {
                                                            newLeadDueDate =
                                                                leadData.anticipatedClosedTime;
                                                        } else if (lead.dueTime === null) {
                                                            newLeadDueDate = null;
                                                        } else {
                                                            newLeadDueDate = lead.dueTime;
                                                        }
                                                    }

                                                    // @ts-expect-error
                                                    let optimisticTags = [];
                                                    if (lead.tags) {
                                                        optimisticTags = lead.tags.map((tag) => ({
                                                            __typename: 'Tag',
                                                            name: tag.name,
                                                            count: 0,
                                                            // @ts-expect-error
                                                            id: tag.id ? tag.id : 'NEW_TAG_ID',
                                                        }));
                                                    } else if (leadData) {
                                                        optimisticTags = leadData.tags;
                                                    }

                                                    if (lead.tags) {
                                                        lead.tags = lead.tags.map((tag) => {
                                                            return {
                                                                name: tag.name,
                                                                tagType: 'LEAD',
                                                            };
                                                        });
                                                    }

                                                    return updateLeadMutation({
                                                        variables: {lead},
                                                        optimisticResponse: {
                                                            __typename: 'Mutation',
                                                            updateLead: {
                                                                __typename: 'UpdateLeadPayload',
                                                                // @ts-expect-error
                                                                lead: {
                                                                    __typename: 'Lead',
                                                                    ...leadData,
                                                                    ...lead,
                                                                    name: leadName,
                                                                    anticipatedClosedTime:
                                                                        newLeadDueDate,
                                                                    // @ts-expect-error
                                                                    tags: optimisticTags,
                                                                },
                                                            },
                                                        },
                                                        update: (proxy, mutationResults) => {
                                                            const updatedLead =
                                                                mutationResults &&
                                                                mutationResults.data &&
                                                                mutationResults.data.updateLead &&
                                                                mutationResults.data.updateLead
                                                                    .lead;

                                                            if (!updatedLead) return;

                                                            proxy.writeFragment({
                                                                id: lead.id,
                                                                fragment: LEAD_FRAGMENT,
                                                                fragmentName: 'Lead',
                                                                data: {
                                                                    ...updatedLead,
                                                                },
                                                            });
                                                        },
                                                    });
                                                }}
                                            />
                                        )}
                                    </UpdateLeadMutation>
                                );
                            }}
                        </LeadQuery>
                    );
                }}
            </TagsQuery>
        )}
    </GetSessionUser>
);
